import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { urls } from '@zapier/identity-utils';
import { defaultRouteTo } from '@zapier/authentication/src/utils';
import { addParamsToUrl } from '@zapier/url-utils';

import { fetch } from '../../../utils';

// `active` / `invalid_or_expired` resolve to redirects, not render states.
export type VerifySignupStatus =
  | 'peeking'
  | 'pending'
  | 'activating'
  | 'redirecting'
  | 'wrong-state' // 409
  | 'rate-limited' // 429
  | 'error';

type PeekState = 'pending' | 'active' | 'invalid_or_expired';

type PeekBody = { state?: PeekState };
type ActivateBody = { redirect?: string };

const loginVerifiedUrl = () => addParamsToUrl(urls.LOGIN_URL, { verified: 1 });

/**
 * Drives the signup activation page (ER-680). On mount it `peek`s (read-only,
 * never consumes) so link-prefetching scanners can't burn the token; only the
 * explicit `activate()` click consumes it.
 */
export function useVerifySignup(token: string) {
  const router = useRouter();
  const [status, setStatus] = useState<VerifySignupStatus>('peeking');

  // Peek at most once across re-renders / StrictMode double-invokes.
  const hasPeeked = useRef(false);

  const goToExpiredPage = useCallback(() => {
    setStatus('redirecting');
    // In-app (identity-fe) route.
    void router.replace(urls.VERIFY_SIGNUP_EXPIRED_URL);
  }, [router]);

  const goToLoginVerified = useCallback((redirect?: string) => {
    setStatus('redirecting');
    // Login lives on the monolith → full-page nav.
    defaultRouteTo(redirect || loginVerifiedUrl());
  }, []);

  useEffect(() => {
    if (!token || hasPeeked.current) {
      return;
    }
    hasPeeked.current = true;

    void (async () => {
      try {
        const res = await fetch<PeekBody>(
          `${urls.VERIFY_SIGNUP_API_URL}/${encodeURIComponent(token)}`
        );

        if (res.status === 429) {
          setStatus('rate-limited');
          return;
        }

        const state = res.body?.state;
        if (res.status === 200 && state === 'pending') {
          setStatus('pending');
        } else if (res.status === 200 && state === 'active') {
          goToLoginVerified();
        } else if (res.status === 200 && state === 'invalid_or_expired') {
          goToExpiredPage();
        } else {
          setStatus('error');
        }
      } catch {
        setStatus('error');
      }
    })();
  }, [token, goToExpiredPage, goToLoginVerified]);

  const activate = useCallback(async () => {
    // Ignore re-clicks while a request is in flight.
    if (status !== 'pending' && status !== 'error') {
      return;
    }
    setStatus('activating');

    try {
      const res = await fetch<ActivateBody>(
        `${urls.VERIFY_SIGNUP_API_URL}/${encodeURIComponent(token)}`,
        { method: 'POST' }
      );

      switch (res.status) {
        case 200:
          goToLoginVerified(res.body?.redirect);
          return;
        case 401:
          goToExpiredPage();
          return;
        case 409:
          setStatus('wrong-state');
          return;
        case 429:
          setStatus('rate-limited');
          return;
        default:
          setStatus('error');
          return;
      }
    } catch {
      setStatus('error');
    }
  }, [token, status, goToExpiredPage, goToLoginVerified]);

  return { status, activate };
}
